import React, {
	useEffect,
	useMemo,
	useState,
} from 'react';
import {
	Drawer,
	Row,
	Col,
	Form,
	Alert,
	Flex,
} from 'antd';
import { FormInstance } from 'antd/lib/form';
import { Moment } from 'moment';
import { useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import {
	Currencies,
	FuelTypes,
	VoyageBunkerTypes,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import { VoyageBunkerEntry } from '@api/utils/sequelize/getVoyageBunkers';
import { useNavigationBlock } from '@client/lib/navigationBlock';
import showErrorNotification from '@client/utils/showErrorNotification';
import { MultiCurrencyValueObject } from '@client/components/MultiCurrencyInput';
import {
	createVoyageBunker,
	updateVoyageBunker,
} from '@client/lib/api';
import getVoyageBunkerForm from '@client/utils/getVoyageBunkerForm';
import { useVoyageData } from '@client/screens/fleet/VoyageDetailsScreen/components/VoyageProvider/VoyageProvider';

type VoyageBunkerFormProps = {
	voyageId: number;
	onClose: () => void;
	initialValues?: Partial<VoyageBunkerEntry> | null | undefined;
	visible: boolean;
	form: FormInstance;
	onSave: () => Promise<void> | void;
	baseCurrency: Values<typeof Currencies>;
};

const VoyageBunkerForm: React.FC<VoyageBunkerFormProps> = ({
	onClose,
	initialValues = null,
	visible,
	form,
	onSave,
	voyageId,
	baseCurrency,
}) => {
	const queryClient = useQueryClient();

	const {
		voyageDetails,
	} = useVoyageData(voyageId);

	const [dirty, setDirty] = useState(false);
	const [saveLoading, setSaveLoading] = useState(false);
	const [multiCurrencyValues, setMultiCurrencyValues] = useState<MultiCurrencyValueObject>(
		{
			currency: initialValues?.currency ?? baseCurrency,
			value: initialValues?.pricePerTon,
			exchangeRate: initialValues?.exchangeRate,
		},
	);

	const {
		useBlocker,
		makeBlockable,
	} = useNavigationBlock();

	useBlocker(dirty && form.isFieldsTouched(true));

	const isEditing = useMemo(() => {
		if (initialValues == null || Object.keys(initialValues).length === 0) {
			return false;
		}

		return true;
	}, [initialValues]);

	const bunkerStemAlert = useMemo(() => {
		if (!initialValues || !initialValues.bunkerStemId || !voyageDetails) {
			return null;
		}

		const portName = initialValues?.BunkerStem?.PortCallAction?.PortCall?.port?.name ?? 'N/A';
		const otherVoyageId = initialValues?.BunkerStem?.origin.id;
		const otherVoyageIdentifier = initialValues?.BunkerStem?.origin.identifier;

		const element = (
			<Flex vertical>
				<p style={{ margin: 0, padding: 0 }}>
					This bunker expense belongs to a bunker stem, and can only be adjusted from the original stem.
					The source stem can be found on the port call {portName} on contract
					<Link style={{ marginLeft: 4 }} to={`/contracts/${otherVoyageId}`}>{otherVoyageIdentifier}</Link>
				</p>
			</Flex>
		);

		return (
			<Alert message={element} />
		);
	}, [initialValues]);

	const forceCloseDrawer = () => {
		form.resetFields();
		onClose();
		setDirty(false);
	};

	const closeDrawer = makeBlockable(forceCloseDrawer);

	const saveBunkers = async (values: {
		currency: Values<typeof Currencies>;
		exchangeRate: number;
		pricePerTon: number;
		type: Values<typeof VoyageBunkerTypes>;
		fuelGrade: Values<typeof FuelTypes>;
		quantity: number;
		minQuantity: number;
		maxQuantity: number;
		date: Moment;
		estimated: boolean;
	}) => {
		try {
			setSaveLoading(true);
			if (initialValues != null && initialValues.id != null) {
				await updateVoyageBunker(voyageId, initialValues.id, values);
			} else if (
				values.currency != null &&
				values.exchangeRate != null &&
				values.pricePerTon != null
			) {
				await createVoyageBunker(voyageId, values);
			}

			await onSave();
			queryClient.invalidateQueries([['tcPnl', voyageId], ['voyageDetails', voyageId]]);
			forceCloseDrawer();
		} catch (e) {
			showErrorNotification('Could not add bunker', e as Error);
		} finally {
			setSaveLoading(false);
		}
	};

	useEffect(() => {
		setDirty(false);
		form.resetFields();
		setMultiCurrencyValues({
			currency: initialValues?.currency ?? baseCurrency,
			value: initialValues?.pricePerTon,
			exchangeRate: initialValues?.exchangeRate,
		});
		if (initialValues != null) {
			form.setFieldsValue({ value: initialValues.pricePerTon });
		}
	}, [baseCurrency, form, initialValues]);

	return (
		<Drawer
			title={isEditing ? 'Edit bunkers' : 'Add bunkers'}
			placement="left"
			onClose={closeDrawer}
			open={visible}
			width={550}
			destroyOnClose
		>
			<Row gutter={16}>
				<Col span={24}>
					<Form
						form={form}
						layout="vertical"
						onFinish={(values) => {
							let transformedValues = { ...values };
							transformedValues = {
								...transformedValues,
								pricePerTon: transformedValues.pricePerTon.value,
								currency: transformedValues.pricePerTon.currency,
								exchangeRate: transformedValues.pricePerTon.exchangeRate,
							};

							saveBunkers(transformedValues);
						}}
						initialValues={initialValues == null ? undefined : initialValues}
						onValuesChange={(changed) => {
							if (changed?.quantity) {
								form.setFieldValue('minQuantity', changed.quantity);
							}

							if (!form.isFieldsTouched(true)) {
								return;
							}

							setDirty(true);
						}} 
						data-tour="bunkersForm"
					>
						{
							getVoyageBunkerForm(
								baseCurrency,
								saveLoading,
								multiCurrencyValues,
								setMultiCurrencyValues,
								initialValues?.disableEdit ?? false,
								bunkerStemAlert,
							)
						}
					</Form>
				</Col>
			</Row>
		</Drawer>
	);
};

export default VoyageBunkerForm;
